/**
 * Transaction submit, approve, and status-poll pipeline.
 *
 * Sits between the write helpers in the api layer and the store. A send is two
 * authenticated calls the gateway fulfills server-side: a create that opens a
 * pending approval, then an approve that drives the MPC sign and broadcast once
 * the approval quorum is met. For a single-approver self-custody wallet the
 * creating user may approve their own transaction, so this one pipeline completes
 * the send for one user. It holds no key, builds no payload, and makes no
 * authorization decision of its own; the gateway is the spend authority.
 */

import {
  createTransaction as apiCreateTransaction,
  approveApproval as apiApproveApproval,
  getApprovals as apiGetApprovals,
  getTransaction as apiGetTransaction,
  ApiError,
  type AuthRecoveryMode,
} from '@/lib/api';
import { createMutationAttemptTracker } from '@/lib/mutationAttempt';
import {
  ApprovalDecisionCoordinationError,
  readApprovalDecisionGuard,
  withApprovalDecisionGuardLock,
} from '@/lib/approvalDecisionGuard';
import { mapActivity, messageFor } from '@/services/wallets';
import {
  clearApprovalDecisionGuard,
  getSlice,
  prependActivity,
  setApprovalDecisionGuard,
} from '@/store/store';
import { deviceAssertionHeaderForApproval } from '@/services/devices';
import type { ApprovalDecisionGuard } from '@/lib/types';

/**
 * A gateway TransactionResponse record. The api helpers return the wire shape
 * verbatim; only the fields this pipeline reads are pinned, and the index
 * signature admits the rest of the record.
 */
export interface TransactionRecord {
  id: string;
  status?: string;
  [key: string]: unknown;
}

/** Validate the required gateway transaction identity at the HTTP boundary. */
function transactionRecordFrom(value: unknown): TransactionRecord {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    !('id' in value) ||
    typeof value.id !== 'string' ||
    value.id.trim() === ''
  ) {
    throw new ApiError('Gateway returned an invalid transaction record.', {
      status: 502,
      code: 'INVALID_TRANSACTION_RESPONSE',
    });
  }
  return value as TransactionRecord;
}

/**
 * A gateway approval record. Only the fields this pipeline matches on are
 * pinned; the index signature admits the rest.
 */
interface ApprovalRecord {
  id: string;
  transaction_id?: string;
  status?: string;
  [key: string]: unknown;
}

/** Validate the approval identity before using a 2xx response as local state. */
function approvalRecordFrom(value: unknown, expectedId: string): ApprovalRecord {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    !('id' in value) ||
    value.id !== expectedId
  ) {
    throw new ApiError('Gateway returned an invalid approval record.', {
      status: 502,
      code: 'INVALID_APPROVAL_RESPONSE',
    });
  }
  return value as ApprovalRecord;
}

/** Account generation used to bind one direct-send approval lifecycle. */
interface DirectApprovalOwner {
  sessionEpoch: number;
  orgId: string;
  userId: string;
}

function currentApprovalOwner(): DirectApprovalOwner | null {
  const { authenticated, sessionEpoch, orgId, userId } = getSlice('accounts').current;
  return authenticated && orgId && userId ? { sessionEpoch, orgId, userId } : null;
}

/** A delayed continuation may act only for the exact account that created it. */
function approvalOwnerIsCurrent(owner: DirectApprovalOwner): boolean {
  const current = getSlice('accounts').current;
  return (
    current.authenticated &&
    current.sessionEpoch === owner.sessionEpoch &&
    current.orgId === owner.orgId &&
    current.userId === owner.userId
  );
}

/** Whether the untrusted wire record carries this user's gateway decision. */
function approvalDecisionByUser(record: ApprovalRecord, userId: string): string | null {
  if (!Array.isArray(record.decisions)) return null;
  for (const value of record.decisions) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const decision = value as Record<string, unknown>;
    if (decision.user_id === userId && typeof decision.action === 'string') {
      return decision.action;
    }
  }
  return null;
}

/** Exact approval row for a transaction, including already-decided rows. */
function approvalForTransaction(records: unknown, txId: string): ApprovalRecord | null {
  if (!Array.isArray(records)) return null;
  return (
    records.find(
      (value): value is ApprovalRecord =>
        !!value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        typeof (value as ApprovalRecord).id === 'string' &&
        String((value as ApprovalRecord).transaction_id) === String(txId),
    ) ?? null
  );
}

/**
 * Options shared by the pipeline functions. The abort signal cancels the
 * in-flight calls and the poll loop when the view is torn down.
 */
export type TransactionProgressStage =
  | 'creating'
  | 'authorizing'
  | 'signing'
  | 'confirming';

interface PipelineOptions {
  signal?: AbortSignal;
  /** Called as soon as create returns, before approval/signing begins. */
  onTransactionCreated?: (transaction: TransactionRecord) => void;
  /** Called for every status record observed by the poll loop. */
  onTransactionStatus?: (transaction: TransactionRecord) => void;
  /** Keep polling past broadcast until the transaction confirms or fails. */
  awaitConfirmation?: boolean;
  /** User-facing phase observer for the create/sign/broadcast pipeline. */
  onProgress?: (stage: TransactionProgressStage) => void;
}

/** Options for the safe approval lookup before any device assertion exists. */
interface ApprovalLookupOptions {
  signal?: AbortSignal;
  authRecovery?: AuthRecoveryMode;
}

/** Progress observers update UI/storage but must never alter transaction flow. */
function notifyTransaction(
  observer: ((transaction: TransactionRecord) => void) | undefined,
  transaction: TransactionRecord,
): void {
  try {
    observer?.(transaction);
  } catch {
    // The transaction pipeline remains authoritative if an observer fails.
  }
}

function notifyProgress(
  observer: ((stage: TransactionProgressStage) => void) | undefined,
  stage: TransactionProgressStage,
): void {
  try {
    observer?.(stage);
  } catch {
    // Presentation observers never alter signing or broadcast behavior.
  }
}

/**
 * The CreateTransactionRequest body the confirm step built. Passed through to
 * the gateway verbatim, so its exact fields are the gateway's contract rather
 * than this module's.
 */
type CreateTransactionRequest = { request_id: string } & Record<string, unknown>;

/**
 * Durable browser record for a create whose outcome may be unknown. The exact
 * non-secret request fields are required to replay the same signed payload;
 * credentials, passkey data, and key material are never stored here.
 */
interface PendingCreateIntent {
  fingerprint: string;
  requestId: string;
  request: CreateTransactionRequest;
}

const CREATE_INTENTS_STORAGE_KEY = 'plantme.transaction.createIntents.v1';
const CREATE_INTENTS_LOCK = 'plantme.transaction.createIntents';

/** Canonical business fields; unsigned bytes are retained but not identity. */
function createIntentMaterial(req: CreateTransactionRequest): string {
  return JSON.stringify([
    req.wallet_id ?? null,
    req.destination ?? null,
    req.amount ?? null,
    req.asset ?? null,
    req.chain ?? null,
    req.note ?? null,
    req.bitcoin_payload ?? null,
  ]);
}

/** Hash the business fields used to find an unresolved logical create. */
async function createIntentFingerprint(req: CreateTransactionRequest): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new ApiError('This browser cannot safely coordinate transaction creation.', {
      status: 0,
      code: 'TRANSACTION_COORDINATION_UNAVAILABLE',
    });
  }
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(createIntentMaterial(req)),
  );
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  );
}

function readCreateIntents(): PendingCreateIntent[] {
  try {
    const raw = localStorage.getItem(CREATE_INTENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) throw new Error('invalid transaction intent store');
    return parsed.filter((value): value is PendingCreateIntent => {
      if (!value || typeof value !== 'object') return false;
      const record = value as Partial<PendingCreateIntent>;
      return (
        typeof record.fingerprint === 'string' &&
        /^[0-9a-f]{64}$/.test(record.fingerprint) &&
        isUuid(record.requestId) &&
        !!record.request &&
        typeof record.request === 'object' &&
        !Array.isArray(record.request)
      );
    });
  } catch {
    throw new ApiError('Transaction recovery storage is unavailable.', {
      status: 0,
      code: 'TRANSACTION_COORDINATION_UNAVAILABLE',
    });
  }
}

function writeCreateIntents(records: PendingCreateIntent[]): void {
  try {
    if (records.length === 0) localStorage.removeItem(CREATE_INTENTS_STORAGE_KEY);
    else localStorage.setItem(CREATE_INTENTS_STORAGE_KEY, JSON.stringify(records));
  } catch {
    throw new ApiError('Transaction recovery storage is unavailable.', {
      status: 0,
      code: 'TRANSACTION_COORDINATION_UNAVAILABLE',
    });
  }
}

export function newTransactionRequestId(): string {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new ApiError('This browser cannot safely identify transaction creation.', {
      status: 0,
      code: 'TRANSACTION_COORDINATION_UNAVAILABLE',
    });
  }
  return crypto.randomUUID();
}

async function withCreateIntentLock<T>(run: () => Promise<T>): Promise<T> {
  if (typeof navigator === 'undefined' || typeof navigator.locks?.request !== 'function') {
    throw new ApiError('This browser cannot safely coordinate transaction creation.', {
      status: 0,
      code: 'TRANSACTION_COORDINATION_UNAVAILABLE',
    });
  }
  return navigator.locks.request(CREATE_INTENTS_LOCK, { mode: 'exclusive' }, run);
}

/** Claim or recover one logical create and persist its exact original payload. */
async function claimCreateIntent(req: CreateTransactionRequest): Promise<PendingCreateIntent> {
  const fingerprint = await createIntentFingerprint(req);
  return withCreateIntentLock(async () => {
    const records = readCreateIntents();
    if (!isUuid(req.request_id)) {
      throw new ApiError('A valid transaction request identity is required.', {
        status: 0,
        code: 'TRANSACTION_REQUEST_ID_REQUIRED',
      });
    }
    const callerRequestId = req.request_id;
    const existing = records.find(
      (record) => record.requestId === callerRequestId,
    );
    if (existing && existing.fingerprint !== fingerprint) {
      throw new ApiError(
        'This transaction request identity was already used for different fields.',
        { status: 0, code: 'TRANSACTION_REQUEST_ID_CONFLICT' },
      );
    }
    if (existing) return existing;
    const intent: PendingCreateIntent = {
      fingerprint,
      requestId: callerRequestId,
      request: { ...req },
    };
    writeCreateIntents([...records, intent]);
    return intent;
  });
}

/**
 * Clear only the exact claim this caller resolved or proved never committed.
 *
 * Cleanup is best-effort for correctness because the caller's logical operation
 * id, rather than fingerprint equality, decides whether a record can be reused.
 * A later operation therefore ignores a stale record carrying a different id.
 */
async function releaseCreateIntent(intent: PendingCreateIntent): Promise<void> {
  try {
    await withCreateIntentLock(async () => {
      const records = readCreateIntents();
      writeCreateIntents(
        records.filter(
          (record) =>
            record.fingerprint !== intent.fingerprint || record.requestId !== intent.requestId,
        ),
      );
    });
  } catch {
    // The server result is already authoritative. The caller-owned operation id
    // prevents this stale record from matching a later operation, so cleanup
    // failure must not turn a known response into an unknown mutation outcome.
  }
}

/** Transaction states which authoritatively remain before approval. */
const PRE_APPROVAL_STATUSES = new Set<string>(['pending', 'pending_approval']);

/**
 * Where the pipeline stopped: a completed approve, a policy hold, a missing
 * approval row the user must act on elsewhere, or the initial submit.
 */
export type SendStage =
  'submitted' | 'pending_policy' | 'approved' | 'no_approval';

/**
 * The outcome of a submit-and-approve run, surfaced to the view so it can render
 * the right status panel.
 */
export interface SendOutcome {
  /** The latest TransactionResponse record. */
  transaction: TransactionRecord;
  /**
   * Where the pipeline stopped: a completed approve, a policy hold, or a missing
   * approval row the user must act on elsewhere.
   */
  stage: SendStage;
}

/**
 * Transaction statuses past which no further polling is useful on any chain.
 * Reaching any of these stops the poll loop.
 */
const TERMINAL = new Set<string>([
  'broadcast',
  'broadcast_unknown',
  'confirmed',
  'failed',
  'rejected',
]);

/** Existing create replays that must not open or resubmit an approval. */
const POST_APPROVAL_STATUSES = new Set<string>([
  'signing',
  'signed',
  'broadcast',
  'broadcast_unknown',
  'confirmed',
  'failed',
  'rejected',
]);

/**
 * Whether a transaction status is terminal for polling purposes. The shared
 * terminal set ends every chain's poll. A signed transaction is only a waypoint:
 * Solana and Sui are both broadcast by the gateway, so the client must continue
 * until the network submission reaches a terminal state.
 *
 * @param status The transaction status.
 * @param _chain The request's chain, retained for the polling API contract.
 * @returns True when polling past this status is useless.
 */
export function isTerminalStatus(status: string, _chain?: string): boolean {
  return TERMINAL.has(status);
}

/** Whether a server record represents an unconfirmed broadcast outcome. */
export function isUnknownBroadcastRecord(
  transaction: { status?: unknown; error_reason?: unknown; [key: string]: unknown },
): boolean {
  const status = String(transaction.status ?? '').toLowerCase();
  if (status === 'broadcast_unknown') return true;
  if (status !== 'failed') return false;
  const reason = String(transaction.error_reason ?? '').toLowerCase();
  const legacyBroadcastPrefix = [
    'broadcast failed:',
    'sui broadcast failed:',
    'tron broadcast failed:',
  ].some((prefix) => reason.startsWith(prefix));
  if (!legacyBroadcastPrefix) return false;

  return [
    'rpc unavailable',
    'already known',
    'already exists',
    'dup_transaction_error',
    'duplicate transaction',
  ].some((marker) => reason.includes(marker));
}

/**
 * Thrown when the gateway's policy engine refuses a transaction outright at
 * create with a 403. Carries a calm, actionable message the Send status panel
 * can show as-is: an expired session surfaces as a 401 the api layer already
 * refreshes and replays, so a 403 here is a policy decision, not an auth
 * failure.
 */
export class PolicyRefusedError extends Error {
  constructor() {
    super(
      'This transaction was refused by your organization policy, so nothing was ' +
        'signed or sent. Review the policy limits, or ask an organization admin ' +
        'if this looks wrong.',
    );
    this.name = 'PolicyRefusedError';
  }
}

/**
 * Statuses that mean the gateway is waiting on a policy step beyond a single
 * approval, so the pipeline stops after create rather than self-approving.
 */
const PENDING_POLICY = 'pending_policy';

/** Default poll budget: ninety ticks at two seconds is roughly three minutes. */
const POLL_TICKS = 90;
const POLL_INTERVAL_MS = 2000;

/**
 * Submit a transaction, then drive it through approval so the gateway signs and
 * broadcasts. Each step updates the store so the activity surface reflects
 * progress live.
 *
 * @param req The CreateTransactionRequest body the confirm step built.
 * @param opts Options; the abort signal cancels the in-flight calls and the
 *   poll loop when the view is torn down.
 * @returns The pipeline outcome.
 */
/**
 * The pipeline state an error escaped from, stamped onto thrown errors so a
 * caller can distinguish a proven pre-create failure, an unknown create outcome,
 * a known transaction still awaiting approval, and an unknown broadcast outcome.
 * Send and Swap share the classifier below so neither can restart creation once
 * a durable transaction may already exist.
 */
export type SendStageReached =
  | 'create'
  | 'create_unknown'
  | 'find_approval'
  | 'device'
  | 'approve_pending'
  | 'approve'
  | 'poll';

/** User-visible safety disposition of a failed transaction pipeline. */
export type TransactionFailureDisposition =
  | 'retryable'
  | 'creation_unknown'
  | 'approval_pending'
  | 'broadcast_unknown';

/** Stable failure metadata consumed by both Send and Swap. */
export interface TransactionFailureInfo {
  disposition: TransactionFailureDisposition;
  transactionId: string | null;
}

/**
 * Classify the pipeline's durable state once, so Send and Swap cannot disagree
 * about whether restarting from create is safe.
 */
export function classifyTransactionFailure(err: unknown): TransactionFailureInfo {
  const source = err && typeof err === 'object' ? err : null;
  const stage = source && 'stageReached' in source ? source.stageReached : null;
  const rawTransactionId =
    source && 'transactionId' in source ? source.transactionId : null;
  const transactionId =
    typeof rawTransactionId === 'string' && rawTransactionId.trim() !== ''
      ? rawTransactionId
      : null;

  if (stage === 'create_unknown') {
    return { disposition: 'creation_unknown', transactionId };
  }
  if (
    stage === 'find_approval' ||
    stage === 'device' ||
    stage === 'approve_pending'
  ) {
    return { disposition: 'approval_pending', transactionId };
  }
  if (stage === 'approve' || stage === 'poll') {
    return { disposition: 'broadcast_unknown', transactionId };
  }
  return { disposition: 'retryable', transactionId };
}

/** Stamp and rethrow. Never overwrites a stage set deeper in the call. */
function stampStage(
  err: unknown,
  stage: SendStageReached,
  transactionId?: string,
): never {
  if (err && typeof err === 'object') {
    if (!('stageReached' in err)) {
      (err as { stageReached?: SendStageReached }).stageReached = stage;
    }
    // The gateway transaction id, once known, rides the SAME error the stage
    // stamp does (B1): an ambiguous escape that carries the id lets the swap
    // path keep polling the REAL transaction — the honest way to resolve
    // "possibly live" — instead of latching a record nothing can advance.
    // Never overwritten for the same reason the stage is not: the deepest
    // stamp is the truthful one.
    if (transactionId && !('transactionId' in err)) {
      (err as { transactionId?: string }).transactionId = transactionId;
    }
  }
  throw err;
}

/**
 * Continue a transaction after this tab or a peer has completed approval.
 * A reconciled terminal record returns immediately; an in-progress record and
 * a locally approved transaction both use the same authoritative poll path.
 */
async function finishApprovalPipeline(
  tx: TransactionRecord,
  chain: string | undefined,
  opts: PipelineOptions,
): Promise<SendOutcome> {
  if (isTerminalStatus(String(tx.status ?? '').toLowerCase(), chain)) {
    return { transaction: tx, stage: 'approved' };
  }

  try {
    notifyProgress(opts.onProgress, 'confirming');
    const last = await pollTransaction(tx.id, {
      signal: opts.signal,
      chain,
      awaitConfirmation: opts.awaitConfirmation,
      onTransactionStatus: opts.onTransactionStatus,
    });
    return { transaction: last, stage: 'approved' };
  } catch (err) {
    // Approval already succeeded locally or in a peer. Losing the poll cannot
    // prove that signing or broadcast stopped.
    stampStage(err, 'poll', tx.id);
  }
}

/**
 * Reconcile a local pre-approval failure against both gateway authorities.
 * This tab's request state cannot establish whether another tab or approver
 * advanced the same durable transaction while the local operation failed.
 */
async function reconcilePostCreateFailure(
  err: unknown,
  tx: TransactionRecord,
  pendingStage: 'find_approval' | 'device' | 'approve_pending',
  expectedApprovalId: string | undefined,
  chain: string | undefined,
  opts: PipelineOptions,
): Promise<SendOutcome> {
  let reconciled: TransactionRecord;
  let stillPending: ApprovalRecord | null;
  try {
    const [transactionValue, pendingApproval] = await Promise.all([
      apiGetTransaction(tx.id, { signal: opts.signal }),
      findApprovalForTransaction(tx.id, { signal: opts.signal }),
    ]);
    reconciled = transactionRecordFrom(transactionValue);
    stillPending = pendingApproval;
    if (reconciled.id !== tx.id) {
      throw new ApiError('Gateway returned a different transaction identity.', {
        status: 502,
        code: 'INVALID_TRANSACTION_RESPONSE',
      });
    }
  } catch {
    // Corrective reads are refresh-only and may themselves fail or be aborted.
    // Without agreement from both authorities, never assert that the durable
    // transaction remains unapproved.
    stampStage(err, 'approve', tx.id);
  }

  const reconciledStatus = String(reconciled.status ?? '').toLowerCase();
  if (!POST_APPROVAL_STATUSES.has(reconciledStatus)) {
    const exactApprovalStillPending =
      stillPending !== null &&
      (expectedApprovalId === undefined || stillPending.id === expectedApprovalId);
    if (
      PRE_APPROVAL_STATUSES.has(reconciledStatus) &&
      exactApprovalStillPending
    ) {
      stampStage(err, pendingStage, tx.id);
    }
    // A transaction read can lag a peer's approval transition. Only agreement
    // with the exact pending approval proves that approval is still actionable.
    stampStage(err, 'approve', tx.id);
  }

  prependActivity(mapActivity(reconciled));
  notifyTransaction(opts.onTransactionStatus, reconciled);
  return finishApprovalPipeline(reconciled, chain, opts);
}

/**
 * Reconcile one persisted direct-send approval attempt by exact user decision.
 * Aggregate pending state never reopens a possibly committed decision.
 */
async function reconcileGuardedApproval(
  err: unknown,
  tx: TransactionRecord,
  guard: ApprovalDecisionGuard,
  owner: DirectApprovalOwner,
  chain: string | undefined,
  opts: PipelineOptions,
  definitiveRefusal = false,
): Promise<SendOutcome> {
  const reconciled = await withApprovalDecisionGuardLock(async () => {
    if (!approvalOwnerIsCurrent(owner)) stampStage(err, 'approve', tx.id);
    let transaction: TransactionRecord;
    let approval: ApprovalRecord | null;
    try {
      const records = await apiGetApprovals({ signal: opts.signal });
      if (!approvalOwnerIsCurrent(owner)) stampStage(err, 'approve', tx.id);
      approval = approvalForTransaction(records, tx.id);
      transaction = transactionRecordFrom(
        await apiGetTransaction(tx.id, { signal: opts.signal }),
      );
      if (!approvalOwnerIsCurrent(owner)) stampStage(err, 'approve', tx.id);
      if (transaction.id !== tx.id) {
        throw new ApiError('Gateway returned a different transaction identity.', {
          status: 502,
          code: 'INVALID_TRANSACTION_RESPONSE',
        });
      }
    } catch {
      stampStage(err, 'approve', tx.id);
    }

    const status = String(transaction.status ?? '').toLowerCase();
    const decision = approval ? approvalDecisionByUser(approval, guard.userId) : null;
    if (decision) {
      if (String(approval?.status ?? '') === 'pending' && decision === 'approved') {
        setApprovalDecisionGuard({ ...guard, state: 'recorded' });
      } else {
        clearApprovalDecisionGuard(guard.orgId, guard.userId, guard.approvalId);
      }
      return { transaction, userDecisionRecorded: true };
    }

    if (
      (approval && String(approval.status ?? '') !== 'pending') ||
      POST_APPROVAL_STATUSES.has(status)
    ) {
      clearApprovalDecisionGuard(guard.orgId, guard.userId, guard.approvalId);
      return { transaction, userDecisionRecorded: false };
    }

    if (
      definitiveRefusal &&
      approval?.id === guard.approvalId &&
      String(approval.status ?? '') === 'pending' &&
      PRE_APPROVAL_STATUSES.has(status)
    ) {
      clearApprovalDecisionGuard(guard.orgId, guard.userId, guard.approvalId);
      stampStage(err, 'approve_pending', tx.id);
    }

    stampStage(err, 'approve', tx.id);
  });

  prependActivity(mapActivity(reconciled.transaction));
  notifyTransaction(opts.onTransactionStatus, reconciled.transaction);
  const status = String(reconciled.transaction.status ?? '').toLowerCase();
  if (reconciled.userDecisionRecorded && PRE_APPROVAL_STATUSES.has(status)) {
    return { transaction: reconciled.transaction, stage: 'approved' };
  }
  return finishApprovalPipeline(reconciled.transaction, chain, opts);
}

export async function submitAndApprove(
  req: CreateTransactionRequest,
  opts: PipelineOptions = {},
): Promise<SendOutcome> {
  const { signal } = opts;
  const requestChain = typeof req.chain === 'string' ? req.chain : undefined;

  // Step one: create the transaction record and its pending approval. A 403
  // here is the policy engine refusing the transaction itself, so it is
  // translated to the calm policy message rather than surfacing a raw gateway
  // string or the shared session-expired wording.
  const createAttempt = createMutationAttemptTracker();
  let createIntent: PendingCreateIntent | null = null;
  let tx: TransactionRecord;
  try {
    notifyProgress(opts.onProgress, 'creating');
    createIntent = await claimCreateIntent(req);
    tx = transactionRecordFrom(
      await apiCreateTransaction(
        { ...createIntent.request, request_id: createIntent.requestId },
        {
          signal,
          authRecovery: 'passkey',
          onRequestAttempt: createAttempt.observe,
        },
      ),
    );
    if (tx.id !== createIntent.requestId) {
      throw new ApiError('Gateway returned a different transaction identity.', {
        status: 502,
        code: 'INVALID_TRANSACTION_RESPONSE',
      });
    }
    await releaseCreateIntent(createIntent);
  } catch (err) {
    if (err instanceof ApiError && err.status === 403 && !err.aborted) {
      if (createIntent) await releaseCreateIntent(createIntent);
      stampStage(new PolicyRefusedError(), 'create');
    }
    if (createIntent && !createAttempt.couldHaveCommitted()) {
      await releaseCreateIntent(createIntent);
    }
    const createMayHaveCommitted = createAttempt.couldHaveCommitted();
    stampStage(
      err,
      createMayHaveCommitted ? 'create_unknown' : 'create',
      createMayHaveCommitted ? createIntent?.requestId : undefined,
    );
  }
  prependActivity(mapActivity(tx));
  notifyTransaction(opts.onTransactionCreated, tx);

  // An idempotent replay may return a transaction that already advanced beyond
  // the approval-opening state. Never search for or resubmit its approval; the
  // existing server record is the authority for the result.
  const createdStatus = String(tx.status ?? '').toLowerCase();
  if (POST_APPROVAL_STATUSES.has(createdStatus)) {
    return { transaction: tx, stage: 'approved' };
  }

  // A policy step-up or time-lock lands in pending_policy. Additional approvals
  // or a release time are required, so the pipeline stops here and surfaces the
  // hold rather than approving past a control the gateway is enforcing.
  if (String(tx.status) === PENDING_POLICY) {
    return { transaction: tx, stage: 'pending_policy' };
  }

  // Step two: find the approval the gateway opened for this transaction. The
  // approval record carries the transaction id, so the match is exact and never
  // approves a different pending transaction.
  let approval;
  try {
    approval = await findApprovalForTransaction(tx.id, {
      signal,
      authRecovery: 'passkey',
    });
  } catch (err) {
    return reconcilePostCreateFailure(
      err,
      tx,
      'find_approval',
      undefined,
      requestChain,
      opts,
    );
  }
  if (!approval) {
    // The approval row is the server's record of what may be signed. If it is
    // absent, do not fabricate a signing trigger; surface the pending state so
    // the user can act from an approvals surface rather than guessing.
    return { transaction: tx, stage: 'no_approval' };
  }

  const approvalOwner = currentApprovalOwner();
  if (!approvalOwner) {
    stampStage(new ApprovalDecisionCoordinationError(), 'device', tx.id);
  }
  const approvalGuard: ApprovalDecisionGuard = {
    orgId: approvalOwner.orgId,
    userId: approvalOwner.userId,
    approvalId: approval.id,
    transactionId: tx.id,
    action: 'approve',
    state: 'uncertain',
    dispatchedAt: Date.now(),
  };

  const existingGuard = readApprovalDecisionGuard(
    approvalOwner.orgId,
    approvalOwner.userId,
    approval.id,
  );
  if (existingGuard) {
    return reconcileGuardedApproval(
      new ApiError('This approval decision is already being reconciled.', { status: 0 }),
      tx,
      existingGuard,
      approvalOwner,
      requestChain,
      opts,
    );
  }

  // Step three: approve. The single-approver self-custody flow sends no
  // cryptographic approval signature; the gateway allows the creator to approve
  // their own transaction when only one approval is required. When the user has
  // an enrolled device passkey, run the device assertion first and carry it as
  // the X-Device-Assertion header so the gateway can verify the transaction was
  // authorized on this device before it signs. A user who cancels the device
  // prompt stops the send rather than approving without the stamp.
  let deviceHeaders;
  try {
    notifyProgress(opts.onProgress, 'authorizing');
    // Give React one event-loop turn to paint the passkey instruction before
    // WebAuthn opens its browser-owned modal over the application.
    await delay(0, signal);
    deviceHeaders = await deviceAssertionHeaderForApproval(
      approval.id,
      signal,
      'passkey',
    );
    if (!approvalOwnerIsCurrent(approvalOwner)) {
      stampStage(
        new ApiError('The signed-in account changed before approval.', {
          status: 401,
          code: 'SESSION_CHANGED',
        }),
        'device',
        tx.id,
      );
    }
  } catch (err) {
    // A cancelled or failed device prompt stops this tab before signing, but a
    // peer may have advanced the durable transaction while the prompt was open.
    return reconcilePostCreateFailure(
      err,
      tx,
      'device',
      approval.id,
      requestChain,
      opts,
    );
  }
  const approveAttempt = createMutationAttemptTracker();
  let guardPersisted = false;
  try {
    notifyProgress(opts.onProgress, 'signing');
    const result = await withApprovalDecisionGuardLock(async () => {
      if (!approvalOwnerIsCurrent(approvalOwner)) {
        stampStage(
          new ApiError('The signed-in account changed before approval.', {
            status: 401,
            code: 'SESSION_CHANGED',
          }),
          'device',
          tx.id,
        );
      }
      const peerGuard = readApprovalDecisionGuard(
        approvalGuard.orgId,
        approvalGuard.userId,
        approvalGuard.approvalId,
      );
      if (peerGuard) return { kind: 'existing' as const, guard: peerGuard };

      const record = await apiApproveApproval(
        approval.id,
        {},
        {
          signal,
          headers: deviceHeaders,
          onRequestAttempt: (event) => {
            if (event.type === 'dispatched' && !guardPersisted) {
              if (!setApprovalDecisionGuard(approvalGuard)) {
                throw new ApprovalDecisionCoordinationError();
              }
              guardPersisted = true;
            }
            approveAttempt.observe(event);
          },
        },
      );
      return {
        kind: 'accepted' as const,
        record: approvalRecordFrom(record, approval.id),
      };
    });

    if (result.kind === 'existing') {
      return reconcileGuardedApproval(
        new ApiError('This approval decision is already being reconciled.', { status: 0 }),
        tx,
        result.guard,
        approvalOwner,
        requestChain,
        opts,
      );
    }

    const returnedStatus = String(result.record.status ?? '');
    const recorded = approvalDecisionByUser(result.record, approvalGuard.userId);
    await withApprovalDecisionGuardLock(async () => {
      if (returnedStatus === 'pending' || recorded === 'approved') {
        setApprovalDecisionGuard({ ...approvalGuard, state: 'recorded' });
      }
      if (returnedStatus !== 'pending') {
        clearApprovalDecisionGuard(
          approvalGuard.orgId,
          approvalGuard.userId,
          approvalGuard.approvalId,
        );
      }
    });
  } catch (err) {
    // THE IRREVERSIBLE BOUNDARY. Only an attempt whose transport outcome could
    // have committed is possibly signed/broadcast through THIS request. A 4xx
    // can still mean another tab approved after our lookup, so it cannot prove
    // the aggregate transaction remained pending.
    const mayHaveCommitted = approveAttempt.couldHaveCommitted();
    if (approveAttempt.wasAccepted() && guardPersisted) {
      await withApprovalDecisionGuardLock(async () => {
        setApprovalDecisionGuard({ ...approvalGuard, state: 'recorded' });
      });
      return finishApprovalPipeline(tx, requestChain, opts);
    }
    if (
      guardPersisted &&
      (mayHaveCommitted || (err instanceof ApiError && err.status === 400))
    ) {
      return reconcileGuardedApproval(
        err,
        tx,
        approvalGuard,
        approvalOwner,
        requestChain,
        opts,
        err instanceof ApiError && err.status === 400,
      );
    }
    if (guardPersisted) {
      await withApprovalDecisionGuardLock(async () => {
        clearApprovalDecisionGuard(
          approvalGuard.orgId,
          approvalGuard.userId,
          approvalGuard.approvalId,
        );
      });
    }
    return reconcilePostCreateFailure(
      err,
      tx,
      'approve_pending',
      approval.id,
      requestChain,
      opts,
    );
  }

  // Step four: poll to a terminal state for status feedback. The request's
  // chain rides along for the shared polling contract; every supported chain
  // now continues past "signed" to broadcast or confirmation.
  return finishApprovalPipeline(tx, requestChain, opts);
}

/**
 * Find the pending approval row for a transaction by its id. Lists the org's
 * approvals and matches on the transaction id the approval carries, so a wallet
 * with several pending transactions never approves the wrong one.
 *
 * @param txId Transaction uuid.
 * @param opts Options.
 * @returns The matching approval, or null when none is pending for this
 *   transaction.
 */
export async function findApprovalForTransaction(
  txId: string,
  opts: ApprovalLookupOptions = {},
): Promise<ApprovalRecord | null> {
  const rows = (await apiGetApprovals({
    signal: opts.signal,
    authRecovery: opts.authRecovery,
  })) as unknown;
  const approval = approvalForTransaction(rows, txId);
  return approval && String(approval.status) === 'pending' ? approval : null;
}

/**
 * Poll one transaction until it reaches a terminal state or the budget expires.
 * Updates the store on each tick so the activity surface advances live. A caller
 * abort (the view tearing down) stops the loop and returns the last observation.
 *
 * @param txId Transaction uuid.
 * @param opts Options. The optional chain refines what counts as terminal. Set
 *   `awaitConfirmation` for dependent transactions (such as ERC-20 approval
 *   followed by a swap) that cannot proceed safely from `broadcast` alone.
 * @returns The last observed TransactionResponse.
 */
export async function pollTransaction(
  txId: string,
  opts: PipelineOptions & { chain?: string } = {},
): Promise<TransactionRecord> {
  const { signal } = opts;
  let last: TransactionRecord | null = null;
  for (let i = 0; i < POLL_TICKS; i += 1) {
    if (signal?.aborted) {
      return last ?? ((await apiGetTransaction(txId)) as TransactionRecord);
    }
    last = (await apiGetTransaction(txId, { signal })) as TransactionRecord;
    prependActivity(mapActivity(last));
    notifyTransaction(opts.onTransactionStatus, last);
    const status = String(last.status);
    const terminal = opts.awaitConfirmation
      ? status === 'confirmed' || status === 'failed' || status === 'rejected'
      : isTerminalStatus(status, opts.chain);
    if (terminal) return last;
    await delay(POLL_INTERVAL_MS, signal);
  }
  // The loop runs at least once, so last is always set by the time it exits.
  return last as TransactionRecord;
}

/**
 * Resolve after a delay, or immediately when the signal aborts, so a poll loop
 * stops promptly on teardown rather than waiting out the interval.
 *
 * @param ms Milliseconds to wait.
 * @param signal Abort signal that resolves the wait early.
 * @returns Resolves after the delay or on abort.
 */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
    }
  });
}

export { messageFor };
